import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { TEAM_QUERY_STALE_TIME_MS } from "@/hooks/useInvalidateTeamQueries";
import { useEveryMinute } from "@/hooks/useNowEveryMinute";
import { uiVisibleErrorKey } from "@/utils/errors";
import type { DayPartHoursSet, Holiday, HolidayDayPart, HolidayGroup, HolidayPerson, HolidaySettings, HolidayStaffCount, HolidayUpsert } from "./holidayApi";
import { holidayApi } from "./holidayApi";
import { holidayDepartmentKey } from "./holidayQueryKeys";

/** How far ahead "upcoming holidays" looks. */
export const UPCOMING_HOLIDAY_MONTHS = 12;
/** Ids per people request, so the query string stays short on a big roster. The server takes at most this many (MAX_PEOPLE_IDS). */
const PEOPLE_CHUNK_SIZE = 200;
const ISO_DATE = "yyyy-MM-dd";

const holidayGroupsKey = (departmentId: string) => [...holidayDepartmentKey(departmentId), "groups"] as const;

/** The settings. The first read sets up groups A and B, so the read-only groups query takes them over at once. */
export const useHolidaySettings = (departmentId: string) => {
	const queryClient = useQueryClient();
	return useQuery<HolidaySettings>({
		queryKey: [...holidayDepartmentKey(departmentId), "settings"],
		queryFn: async () => {
			const settings = await holidayApi.getSettings(departmentId);
			// The Team list shows the holiday columns and filters as soon as the groups exist, without a refresh.
			queryClient.setQueryData<HolidayGroup[]>(holidayGroupsKey(departmentId), settings.groups);
			return settings;
		},
	});
};

/** `enabled: false` makes no request: the schedule passes it for users the head-nurse-only endpoints would refuse. */
export type HolidayQueryOptions = { enabled: boolean };

/** The groups without setting them up — for pages that only show them, like the Team list. */
export const useHolidayGroups = (
	departmentId: string,
	// DEFAULT: no options means the caller may always read them, as the Team pages do.
	options: HolidayQueryOptions = { enabled: true },
) =>
	useQuery<HolidayGroup[]>({
		queryKey: holidayGroupsKey(departmentId),
		queryFn: () => holidayApi.getGroups(departmentId),
		enabled: options.enabled,
	});

/** Holidays dated `from`..`to` (both included). */
export const useHolidays = (
	departmentId: string,
	from: DateTime,
	to: DateTime,
	// DEFAULT: no options means the caller may always read them, as the Team pages do.
	options: HolidayQueryOptions = { enabled: true },
) => {
	const fromIso = from.toFormat(ISO_DATE);
	const toIso = to.toFormat(ISO_DATE);
	return useQuery<Holiday[]>({
		queryKey: [...holidayDepartmentKey(departmentId), "holidays", fromIso, toIso],
		queryFn: () => holidayApi.getHolidays(departmentId, fromIso, toIso),
		enabled: options.enabled,
	});
};

/** How many people Apply schedules on each holiday, by job. Refreshed with every holiday or group change. */
export const useHolidayStaffCounts = (departmentId: string, from: DateTime, to: DateTime) => {
	const fromIso = from.toFormat(ISO_DATE);
	const toIso = to.toFormat(ISO_DATE);
	return useQuery<HolidayStaffCount[]>({
		queryKey: [...holidayDepartmentKey(departmentId), "staff-counts", fromIso, toIso],
		queryFn: () => holidayApi.getStaffCounts(departmentId, fromIso, toIso),
	});
};

const isoToday = (timezone: string): string => DateTime.now().setZone(timezone).toFormat(ISO_DATE);

/** Today in the department's timezone. Checked every minute and when the page comes back into view, so it moves on at midnight. */
export const useDepartmentToday = (): DateTime => {
	const { timezone } = useHospitalData();
	const [checked, setChecked] = useState(() => ({ timezone, isoDay: isoToday(timezone) }));
	useEveryMinute(() =>
		setChecked((current) => {
			const isoDay = isoToday(timezone);
			// Same day: the same object, so nothing re-renders.
			return current.timezone === timezone && current.isoDay === isoDay ? current : { timezone, isoDay };
		}),
	);
	// Another department's timezone can be on another date, so a switch doesn't wait for the next check.
	const isoDay = checked.timezone === timezone ? checked.isoDay : isoToday(timezone);
	return useMemo(() => DateTime.fromFormat(isoDay, ISO_DATE, { zone: timezone }), [isoDay, timezone]);
};

/** Holidays from today through the next 12 months, in date order. */
export const useUpcomingHolidays = (departmentId: string) => {
	const today = useDepartmentToday();
	const query = useHolidays(departmentId, today, today.plus({ months: UPCOMING_HOLIDAY_MONTHS }));
	const upcoming = useMemo(() => (query.data === undefined ? undefined : [...query.data].sort((a, b) => a.date.toMillis() - b.date.toMillis())), [query.data]);
	return { ...query, data: upcoming };
};

/**
 * Every ACTIVE person of the department. Deactivated people keep their group but are left out.
 * Kept fresh as long as the roster: each read works out six weeks of shifts per person, and every
 * holiday edit invalidates the department key, so changes still show at once.
 */
export const useActiveHolidayPeople = (departmentId: string) =>
	useQuery<HolidayPerson[]>({
		queryKey: [...holidayDepartmentKey(departmentId), "people", "active"],
		queryFn: () => holidayApi.getPeople(departmentId, null),
		staleTime: TEAM_QUERY_STALE_TIME_MS,
	});

export const chunkIds = (ids: readonly number[], size: number): number[][] => {
	const chunks: number[][] = [];
	for (let start = 0; start < ids.length; start += size) chunks.push(ids.slice(start, start + size));
	return chunks;
};

export type PeopleChunkResult = { data?: HolidayPerson[]; isError: boolean; isPending: boolean };
export type HolidayPeopleByIds = { data: ReadonlyMap<number, HolidayPerson> | undefined; isError: boolean; isLoading: boolean };

/** Joins the chunks into one map once every chunk has loaded. Module-level, so its result stays memoized. */
export const combinePeopleChunks = (results: PeopleChunkResult[]): HolidayPeopleByIds => {
	const isError = results.some((result) => result.isError);
	const isLoading = results.some((result) => result.isPending);
	if (isError || isLoading) return { data: undefined, isError, isLoading };
	const byNurseId = new Map<number, HolidayPerson>();
	for (const result of results) {
		// A loaded chunk always has data; the check only narrows the type.
		if (result.data === undefined) continue;
		for (const person of result.data) byNurseId.set(person.nurseId, person);
	}
	return { data: byNurseId, isError, isLoading };
};

/**
 * The given people, deactivated ones included, keyed by nurse id. `data` is undefined until every chunk
 * has loaded, so a half-loaded roster never reads as "no group".
 */
export const useHolidayPeopleByIds = (departmentId: string, nurseIds: readonly number[]) => {
	const chunks = useMemo(
		() =>
			chunkIds(
				[...nurseIds].sort((a, b) => a - b),
				PEOPLE_CHUNK_SIZE,
			),
		[nurseIds],
	);
	return useQueries({
		queries: chunks.map((chunk) => ({
			queryKey: [...holidayDepartmentKey(departmentId), "people", "ids", chunk.join(",")],
			queryFn: () => holidayApi.getPeople(departmentId, chunk),
			// Same as the active people above: the Team page doesn't redo the calculation on every visit.
			staleTime: TEAM_QUERY_STALE_TIME_MS,
		})),
		combine: combinePeopleChunks,
	});
};

/** Shown when the server gives no message of its own (network down, a bug). */
const SAVE_FAILED_KEY = "holidays.error.saveFailed";

/**
 * One write: tracks `{event}_start/_success/_error`, refreshes every holiday query of the department, and
 * shows the server's message (or a generic one) when it fails.
 */
const useHolidayMutation = <TVariables, TResult>(event: string, write: (departmentId: string, variables: TVariables) => Promise<TResult>) => {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	return useMutation<TResult, unknown, TVariables>({
		mutationFn: (variables) => {
			track(`${event}_start`, { department_id: departmentId });
			return write(departmentId, variables);
		},
		onSuccess: async () => {
			track(`${event}_success`, { department_id: departmentId });
			await queryClient.invalidateQueries({ queryKey: holidayDepartmentKey(departmentId) });
		},
		onError: (error) => {
			const messageKey = uiVisibleErrorKey(error);
			trackError(`${event}_error`, { department_id: departmentId, error_key: messageKey });
			// DEFAULT: an error without a server message (network, bug) still needs words for the user.
			showSnackbar(t(messageKey ?? SAVE_FAILED_KEY), "error");
		},
	});
};

export const useSetHolidayGroupCount = () =>
	useHolidayMutation("holiday_group_count_change", (departmentId, count: number) => holidayApi.setGroupCount(departmentId, count));

export const useSaveDayPartHours = () =>
	useHolidayMutation("holiday_day_part_hours_save", (departmentId, jobs: { jobId: string; parts: DayPartHoursSet }[]) =>
		holidayApi.saveDayPartHours(departmentId, jobs),
	);

export const useCreateHoliday = () => useHolidayMutation("holiday_create", (departmentId, body: HolidayUpsert) => holidayApi.createHoliday(departmentId, body));

export const useEditHoliday = () =>
	useHolidayMutation("holiday_edit", (departmentId, { holidayId, body }: { holidayId: string; body: HolidayUpsert }) =>
		holidayApi.editHoliday(departmentId, holidayId, body),
	);

export const useSetHolidaySelectedGroup = () =>
	useHolidayMutation("holiday_selected_group_change", (departmentId, { holidayId, groupId }: { holidayId: string; groupId: string | null }) =>
		holidayApi.setSelectedGroup(departmentId, holidayId, groupId),
	);

export const useDeleteHoliday = () =>
	useHolidayMutation("holiday_delete", (departmentId, { holidayId, smsMessage }: { holidayId: string; smsMessage: string | null }) =>
		holidayApi.deleteHoliday(departmentId, holidayId, smsMessage),
	);

export const useApplyHolidays = () =>
	useHolidayMutation("holiday_apply", (departmentId, { holidayIds, smsMessage }: { holidayIds: string[]; smsMessage: string | null }) =>
		holidayApi.applyHolidays(departmentId, holidayIds, smsMessage),
	);

export type MoveToHolidayGroupVariables = { nurseIds: number[]; groupId: string | null; dayPart: HolidayDayPart | null; smsMessage: string | null };

export const useMoveToHolidayGroup = () =>
	useHolidayMutation("holiday_group_move", (departmentId, { nurseIds, groupId, dayPart, smsMessage }: MoveToHolidayGroupVariables) =>
		holidayApi.moveToGroup(departmentId, nurseIds, groupId, dayPart, smsMessage),
	);

export type SetHolidayDayPartVariables = { nurseId: number; change: { dayPart: HolidayDayPart } | { useCalculated: true } };

export const useSetHolidayDayPart = () =>
	useHolidayMutation("holiday_day_part_change", (departmentId, { nurseId, change }: SetHolidayDayPartVariables) =>
		holidayApi.setDayPart(departmentId, nurseId, change),
	);
